/**
 * LocaleSelectorModal Component
 * Modal overlay for selecting country and language.
 * Uses custom dropdowns to render SVG flag images alongside text.
 */

import { useState, useEffect, useRef } from "react";
import type { CountryWithCurrency, LocaleOptions } from "@/api/locale";
import { useLocalizedTexts, t } from "@/utils/translations";
import { setLocale } from "@/utils/locale-store";
import {
  buildLocalePath,
  getCountryFlagSrc,
  getLanguageFlagSrc,
} from "@/utils/locale-url";
import { useAlternateUrls } from "@/contexts/AlternateUrlsContext";
import { toSafeRelativePath } from "@/utils/url";
import { getEnv } from "@/config/env";
import styles from "./LocaleSelectorModal.module.css";

interface LocaleSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  localeOptions: LocaleOptions;
  currentLanguage: string;
  currentCountry: string;
}

/** Inline flag image (20x15, covers the rectangle nicely for country flags). */
/** Flag image resolved from a country code (e.g. "SE", "EN"). */
function CountryFlagImg({ countryCode }: { countryCode: string }) {
  const src = getCountryFlagSrc(countryCode);
  if (!src) return null;
  return (
    <img src={src} alt="" width={20} height={20} className={styles.flagImg} />
  );
}

/** Flag image resolved from a language code (e.g. "en" → GB, "sv" → SE). */
function LanguageFlagImg({ languageCode }: { languageCode: string }) {
  const src = getLanguageFlagSrc(languageCode);
  if (!src) return null;
  return (
    <img src={src} alt="" width={20} height={20} className={styles.flagImg} />
  );
}

export function LocaleSelectorModal({
  isOpen,
  onClose,
  localeOptions,
  currentLanguage,
  currentCountry,
}: LocaleSelectorModalProps) {
  const localizedTexts = useLocalizedTexts();
  const alternateUrls = useAlternateUrls();
  const overlayRef = useRef<HTMLDivElement>(null);

  const [selectedCountry, setSelectedCountry] = useState(currentCountry);
  const [selectedCulture, setSelectedCulture] = useState(currentLanguage);
  const [countryOpen, setCountryOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const countryRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCountry(currentCountry);
      setSelectedCulture(currentLanguage);
      setCountryOpen(false);
      setLanguageOpen(false);
    }
  }, [isOpen, currentCountry, currentLanguage]);

  // Close on Escape, trap focus within the modal, and restore focus on close
  useEffect(() => {
    if (!isOpen) return;

    // Save the element that had focus before the modal opened
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    function getFocusable(): HTMLElement[] {
      const container = overlayRef.current;
      if (!container) return [];
      return Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    }

    // Move focus into the modal
    const focusable = getFocusable();
    focusable[0]?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const items = getFocusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !overlayRef.current?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !overlayRef.current?.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Restore focus to the opener
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    };
  }, [isOpen, onClose]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        countryRef.current &&
        !countryRef.current.contains(e.target as Node)
      ) {
        setCountryOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(e.target as Node)
      ) {
        setLanguageOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  if (!isOpen) return null;

  const countries = localeOptions.countries;
  const languages = localeOptions.languages;

  // Get valid cultures for the selected country.
  const country = countries.find((c) => c.countryCode === selectedCountry);
  const validCultures = country?.validCultures ?? [];

  // When country changes, auto-select first valid culture for that country
  function handleCountryChange(newCountryCode: string) {
    setSelectedCountry(newCountryCode);
    setCountryOpen(false);

    const newCountry = countries.find((c) => c.countryCode === newCountryCode);
    const firstCulture = newCountry?.validCultures[0];
    if (firstCulture) {
      setSelectedCulture(firstCulture.cultureCode);
    }
  }

  function handleLanguageChange(cultureCode: string) {
    setSelectedCulture(cultureCode);
    setLanguageOpen(false);
  }

  // Move focus between option <li> siblings with ArrowUp/ArrowDown (wraps at ends)
  function handleOptionArrowKeys(e: React.KeyboardEvent<HTMLLIElement>) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(
      e.currentTarget.parentElement?.children ?? []
    ) as HTMLElement[];
    const idx = items.indexOf(e.currentTarget);
    const next =
      e.key === "ArrowDown"
        ? items[(idx + 1) % items.length]
        : items[(idx - 1 + items.length) % items.length];
    next?.focus();
  }

  function handleApply() {
    // Look up the alternate URL for the target locale (correct translated slug).
    // The backend returns culture-prefixed URLs (e.g. "/nb-no/tracker-og-wehunt/"),
    // absolute when the culture is bound to a domain. It is still content
    // data, so only a path on the destination origin is ever navigated to —
    // anything else (javascript:, a foreign host) falls back to the start page.
    const rawAlternateUrl =
      alternateUrls[selectedCulture] ??
      Object.entries(alternateUrls).find(
        ([key]) => key.toLowerCase() === selectedCulture.toLowerCase()
      )?.[1];

    // Norwegian is served from a separate deployment (wehunt.no), so jump there
    // instead of staying on the current site. The base URL is per-environment.
    if (selectedCulture.toLowerCase() === "nb-no") {
      const baseUrl = getEnv().norwegianRedirectUrl;
      const alternatePath = toSafeRelativePath(rawAlternateUrl, baseUrl);
      window.location.href = alternatePath
        ? new URL(alternatePath, baseUrl).href
        : baseUrl;
      return;
    }

    const alternatePath = toSafeRelativePath(
      rawAlternateUrl,
      window.location.origin
    );

    // Look up currency from locale options
    let currency: string | undefined;
    if (localeOptions) {
      const localeCountry = localeOptions.countries.find(
        (c) => c.countryCode === selectedCountry
      );
      if (localeCountry?.currencyCode) {
        currency = localeCountry.currencyCode;
      }
    }

    // Persist to localStorage
    setLocale({
      language: selectedCulture,
      country: selectedCountry,
      ...(currency ? { currency } : {}),
    });

    if (alternatePath) {
      window.location.href = alternatePath;
    } else {
      window.location.href = buildLocalePath(selectedCulture, "/");
    }
  }

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }

  // Get country display name in current language
  function getCountryDisplayName(c: CountryWithCurrency): string {
    const langPrefix = currentLanguage.split("-")[0];
    return (
      c.displayNames.find((d) => d.languageCode === langPrefix)?.name ??
      c.displayNames[0]?.name ??
      c.countryCode
    );
  }

  // Resolve the display name for the currently selected country
  const selectedCountryObj = countries.find(
    (c) => c.countryCode === selectedCountry
  );
  const selectedCountryLabel = selectedCountryObj
    ? getCountryDisplayName(selectedCountryObj)
    : selectedCountry;

  // Resolve the display name for the currently selected language
  const selectedCultureObj = validCultures.find(
    (vc) => vc.cultureCode === selectedCulture
  );
  const selectedLangObj = selectedCultureObj
    ? languages.find((l) => l.languageCode === selectedCultureObj.languageCode)
    : null;
  const selectedLanguageLabel = selectedLangObj?.displayName ?? selectedCulture;
  const selectedLanguageCode =
    selectedCultureObj?.languageCode ?? selectedCulture;

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={t(
        localizedTexts,
        "Tracker.Generic.SelectCountryAndLanguage",
        "Select country and language"
      )}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {t(
              localizedTexts,
              "Tracker.Generic.SelectYourLocation",
              "Select your location"
            )}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t(localizedTexts, "Tracker.Generic.Close", "Close")}
          >
            &times;
          </button>
        </div>

        <div className={styles.body}>
          {/* Country Dropdown */}
          <div className={styles.field}>
            <label className={styles.label}>
              {t(localizedTexts, "Tracker.Checkout.Address.Country", "Country")}
            </label>
            <div className={styles.dropdown} ref={countryRef}>
              <button
                type="button"
                className={styles.dropdownTrigger}
                onClick={() => {
                  setCountryOpen((v) => !v);
                  setLanguageOpen(false);
                }}
                aria-expanded={countryOpen}
                aria-haspopup="listbox"
              >
                <CountryFlagImg countryCode={selectedCountry} />
                <span className={styles.dropdownLabel}>
                  {selectedCountryLabel}
                </span>
                <svg
                  className={styles.dropdownChevron}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {countryOpen && (
                <ul className={styles.dropdownList} role="listbox">
                  {countries.map((c) => (
                    <li
                      key={c.countryCode}
                      role="option"
                      aria-selected={c.countryCode === selectedCountry}
                      tabIndex={0}
                      className={`${styles.dropdownItem} ${c.countryCode === selectedCountry ? styles.dropdownItemSelected : ""}`}
                      onClick={() => handleCountryChange(c.countryCode)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleCountryChange(c.countryCode);
                        } else {
                          handleOptionArrowKeys(e);
                        }
                      }}
                    >
                      <CountryFlagImg countryCode={c.countryCode} />
                      <span>{getCountryDisplayName(c)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Language Dropdown */}
          <div className={styles.field}>
            <label className={styles.label}>
              {t(localizedTexts, "Tracker.Generic.Language", "Language")}
            </label>
            <div className={styles.dropdown} ref={languageRef}>
              <button
                type="button"
                className={styles.dropdownTrigger}
                onClick={() => {
                  setLanguageOpen((v) => !v);
                  setCountryOpen(false);
                }}
                aria-expanded={languageOpen}
                aria-haspopup="listbox"
              >
                <LanguageFlagImg languageCode={selectedLanguageCode} />
                <span className={styles.dropdownLabel}>
                  {selectedLanguageLabel}
                </span>
                <svg
                  className={styles.dropdownChevron}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {languageOpen && (
                <ul className={styles.dropdownList} role="listbox">
                  {validCultures.map((vc) => {
                    const lang = languages.find(
                      (l) => l.languageCode === vc.languageCode
                    );
                    return (
                      <li
                        key={vc.cultureCode}
                        role="option"
                        aria-selected={vc.cultureCode === selectedCulture}
                        tabIndex={0}
                        className={`${styles.dropdownItem} ${vc.cultureCode === selectedCulture ? styles.dropdownItemSelected : ""}`}
                        onClick={() => handleLanguageChange(vc.cultureCode)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleLanguageChange(vc.cultureCode);
                          } else {
                            handleOptionArrowKeys(e);
                          }
                        }}
                      >
                        <LanguageFlagImg languageCode={vc.languageCode} />
                        <span>{lang?.displayName ?? vc.cultureCode}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
          >
            {t(localizedTexts, "Tracker.Generic.Cancel", "Cancel")}
          </button>
          <button
            type="button"
            className={styles.applyButton}
            onClick={handleApply}
          >
            {t(localizedTexts, "Tracker.Generic.Apply", "Apply")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LocaleSelectorModal;
